import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import withFormStore, { useForm } from "../Form/withFormStore";
import Modal from "../Modals/Modal";
import EmailField from "../Form/Field/EmailField";
import Button from "../Buttons/Button";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";

import { sendUnlockInstructions } from "../../mutations/SendUnlockInstructionsMutation";
import { useFormatMessage } from "../Intl/Context";

const AuthenticationUnlockForm = props => {
  const { onClose, email } = props;
  const { formState, validateForm } = useForm();

  const t = useFormatMessage();
  const labels = {
    title: t(intlMessages.title),
    description: t(intlMessages.description),
    requiredFieldsHint: t(formPageIntlMessages.requiredFieldsHint),
    email: t(intlMessages.email),
    submit: t(intlMessages.submit),
    cancel: t(intlMessages.cancel),
    successMessage: t(intlMessages.successMessage)
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formClassName = classNames({
    "authentication-fieldset": true,
    "authentication-sign-in": true,
    box: true,
    "margin-bottom-xs": true,
    "padding-x-lg": true,
    "padding-y-md": true,
    loading: isSubmitting
  });

  const breadcrumbs = [{ title: labels.title }];

  const handleSendResponse = useCallback(() => {
    setIsSubmitting(false);

    onClose(true, labels.successMessage);
  }, [labels.successMessage, onClose]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      // Do not submit form if it is already submitting
      if (isSubmitting) return;

      if (validateForm()) {
        setIsSubmitting(true);

        sendUnlockInstructions(formState, handleSendResponse);
      }
    },
    [formState, handleSendResponse, isSubmitting, validateForm]
  );

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      breadcrumbs={breadcrumbs}
      title={labels.title}
      className="modal-sm"
    >
      <form
        className={formClassName}
        onSubmit={handleSubmit}
        data-send-unlock-instructions-form
      >
        <p className="help-block">{labels.description}</p>

        <p
          className="form-hint-required-fields margin-bottom-sm text-small"
          dangerouslySetInnerHTML={{ __html: labels.requiredFieldsHint }}
        />

        <EmailField
          fieldName="email"
          label={labels.email}
          required
          defaultValue={email}
          autoComplete="email"
        />

        <Button
          type="submit"
          className="btn btn-primary btn-block-md box-shadow-lg"
          onClick={handleSubmit}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          label={labels.submit}
          iconClassName="icon-arrow-right"
          iconPosition="after"
          data-submit-button
        />

        <Button
          type="button"
          className="btn btn-primary-inverse btn-block-md box-shadow-lg"
          onClick={onClose}
          label={labels.cancel}
          disabled={isSubmitting}
          data-loading={isSubmitting}
          iconClassName="icon-arrow-right"
          iconPosition="after"
        />
      </form>
    </Modal>
  );
};

export default withFormStore(AuthenticationUnlockForm);

const intlMessages = defineMessages({
  title: {
    id: "authentication.unlockForm.title",
    defaultMessage: "Renvoi du mail de déblocage de compte"
  },
  description: {
    id: "authentication.unlockForm.description",
    defaultMessage: `
      Un e-mail vous sera envoyé avec les instructions vous permettant de débloquer votre
      compte.
    `
  },
  email: {
    id: "authentication.unlockForm.labels.email",
    defaultMessage: "E-mail"
  },
  submit: {
    id: "authentication.unlockForm.submit",
    defaultMessage: "Envoyer le mail"
  },
  cancel: {
    id: "authentication.passwordForm.cancel",
    defaultMessage: "Annuler"
  },
  successMessage: {
    id: "authentication.unlockForm.successMessage",
    defaultMessage: `
      Si l'adresse e-mail renseignée est bien associée à un compte existant et que le 
      compte est actuellement bloqué, un e-mail a été envoyé avec les instructions de 
      déblocage de votre compte.
    `
  }
});
